import { notify } from '@billy/design-system'
import { trackError } from '@billy/track-error'
import { AxiosResponse } from 'axios'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'

import { FetchQuoteResponseData, updateQuoteStatus } from '@features/invoices/query-api'

import { NotificationKeys } from '../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../enums/queryKeys'

export const useUpdateQuoteStatus = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { mutate, isLoading: isProcessing } = useMutation(updateQuoteStatus, {
    onMutate: async ({ quoteId, value }) => {
      await queryClient.cancelQueries([QueryKeys.Quote, quoteId])

      const previousQuote = queryClient.getQueryData<AxiosResponse<FetchQuoteResponseData>>([QueryKeys.Quote, quoteId])

      // optimistic update
      if (previousQuote) {
        queryClient.setQueryData([QueryKeys.Quote, quoteId], {
          ...previousQuote,
          data: {
            ...previousQuote.data,
            status: {
              ...previousQuote.data.status,
              status: value,
            },
          },
        })
      }
      return { previousQuote }
    },
    onSuccess() {
      notify({
        id: NotificationKeys.QuoteStatusUpdated,
        message: t('quotes.view.update_status.notification.success'),
        variant: 'success',
      })
    },
    // If the mutation fails, roll back using the context returned from onMutate
    onError(error, { quoteId }, context) {
      if (context?.previousQuote) {
        queryClient.setQueryData([QueryKeys.Quote, quoteId], context.previousQuote)
      }

      notify({
        id: NotificationKeys.QuoteStatusUpdated,
        message: t('quotes.view.update_status.notification.error'),
        variant: 'error',
      })

      trackError(error)
    },
    // always refetch after error or success
    onSettled(data, err, { quoteId }) {
      queryClient.invalidateQueries([QueryKeys.Quote, quoteId])
      queryClient.invalidateQueries(QueryKeys.QuotesSummary)
      queryClient.invalidateQueries(QueryKeys.Quotes)
    },
  })

  return { updateQuoteStatus: mutate, isProcessing }
}
